import { useCallback, useEffect, useState } from "react";
import { Disclosure, SupportedLanguage } from "src/components/disclosures/disclosure.types";
import { DEFAULT_LANGUAGE } from "src/components/disclosures/disclosures.config";
import { useUserSlice } from "src/store/features/user.slice";
import { usStateCode } from "src/utils/usStates";
import { useLegalDisclosure } from "./useLegalDisclosure";

interface UseStateDisclosureReturn {
  /** Whether the disclosure modal is open */
  isOpen: boolean;
  /** The selected disclosure to display */
  selectedDisclosure: Disclosure | null;
  /** Backend-resolved ISO region (e.g. "CA") — recorded on acceptance. */
  region?: string;
  /** The currently selected language */
  selectedLanguage: SupportedLanguage;
  /** Handler to close the modal */
  handleClose: () => void;
  /** Handler to accept the disclosure */
  handleAccept: (disclosureId: string) => void;
  /** Handler to change the language */
  handleLanguageChange: (language: SupportedLanguage) => void;
  /** Backend-driven version of the resolved disclosure (for recording acceptance). */
  disclosureVersion?: string;
  /** Modal component props for easy rendering */
  modalProps: {
    open: boolean;
    onClose: () => void;
    disclosure: Disclosure | null;
    selectedLanguage: SupportedLanguage;
    onLanguageChange: (language: SupportedLanguage) => void;
    onAccept: (disclosureId: string) => void;
  };
}

/**
 * Custom hook to manage state-specific disclosure modals.
 * Jurisdiction is resolved server-side from the published legal documents.
 *
 * @param onAcceptCallback - Optional callback function to execute when disclosure is accepted
 * @param autoOpen - Whether to automatically open the modal when disclosure is found (default: true)
 * @returns Object containing modal state, handlers, and props for rendering the DisclosureContentModal
 *
 * @example
 * ```tsx
 * const { modalProps, selectedDisclosure } = useStateDisclosure((disclosureId) => {
 *   console.log('Disclosure accepted:', disclosureId);
 * }, false); // Don't auto-open
 *
 * return (
 *   <>
 *     {selectedDisclosure && <DisclosureContentModal {...modalProps} />}
 *   </>
 * );
 * ```
 */
export const useStateDisclosure = (
  onAcceptCallback?: (disclosureId: string) => void,
  autoOpen: boolean = true,
): UseStateDisclosureReturn => {
  const { stateDisclosures } = useUserSlice();

  const [isOpen, setIsOpen] = useState(false);
  const [selectedDisclosure, setSelectedDisclosure] = useState<Disclosure | null>(null);
  const [selectedLanguage, setSelectedLanguage] = useState<SupportedLanguage>(DEFAULT_LANGUAGE);

  const { disclosure: resolvedDisclosure, document } = useLegalDisclosure({ type: "state-disclosure" });
  const disclosureVersion = document?.version;
  const region = resolvedDisclosure?.region;

  useEffect(() => {
    // Records hold ISO codes ("CA") or legacy full names ("california") — normalize both
    const hasAcceptedDisclosure =
      region != null &&
      stateDisclosures?.some(
        (disclosure) =>
          usStateCode(disclosure.state) === usStateCode(region) &&
          disclosure.accepted === true &&
          (disclosureVersion == null || disclosure.disclosureVersion === disclosureVersion),
      );

    // Don't show disclosure if user has already accepted it
    if (hasAcceptedDisclosure) {
      setSelectedDisclosure(null);
      setIsOpen(false);
      return;
    }

    if (resolvedDisclosure) {
      setSelectedDisclosure(resolvedDisclosure);
      if (autoOpen) {
        setIsOpen(true);
      }
    }
  }, [region, stateDisclosures, autoOpen, resolvedDisclosure, disclosureVersion]);

  // Handle closing the disclosure modal
  const handleClose = useCallback(() => {
    setIsOpen(false);
  }, []);

  // Handle accepting the disclosure
  const handleAccept = useCallback(
    (disclosureId: string) => {
      if (onAcceptCallback) {
        onAcceptCallback(disclosureId);
      }
      setIsOpen(false);
    },
    [onAcceptCallback],
  );

  // Handle language change
  const handleLanguageChange = useCallback((language: SupportedLanguage) => {
    setSelectedLanguage(language);
  }, []);

  // Modal props for easy rendering
  const modalProps = {
    open: isOpen,
    onClose: handleClose,
    disclosure: selectedDisclosure,
    selectedLanguage,
    onLanguageChange: handleLanguageChange,
    onAccept: handleAccept,
  };

  return {
    isOpen,
    selectedDisclosure,
    region,
    selectedLanguage,
    handleClose,
    handleAccept,
    handleLanguageChange,
    disclosureVersion,
    modalProps,
  };
};

export default useStateDisclosure;
